import * as m from '@staiter/i18n';
import type { BankStatementFormat, DraftPaymentCandidate } from '@staiter/shared';
import { useMutation } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { FileUp } from 'lucide-react';
import { type JSX, useRef, useState } from 'react';
import { PageHeader } from '~/components/data';
import { Button, Select } from '~/components/primitives';
import { commitBankImport, previewBankImport } from '~/lib/api/bank-import';
import { formatMoney } from '~/lib/format';
import styles from './_auth.admin.bank-import.module.css';

/**
 * C.6 — Wizard: upload → preview → commit.
 * Minimal 3-step flow: (1) upload file + choose format → (2) preview rows с matching →
 * (3) operator fixes counterparty_ids → commit.
 */

export const Route = createFileRoute('/_auth/admin/bank-import')({
  component: BankImportWizard,
});

function BankImportWizard(): JSX.Element {
  const [format, setFormat] = useState<BankStatementFormat>('kaspi_xlsx');
  const [preview, setPreview] = useState<{
    importId: string;
    filename: string;
    candidates: DraftPaymentCandidate[];
    reconcileToken: string;
    expiresAt: string;
  } | null>(null);
  const [edits, setEdits] = useState<Record<number, string>>({}); // bank_row_no → counterparty_id
  const fileRef = useRef<HTMLInputElement>(null);

  const previewMut = useMutation({
    mutationFn: async (file: File) => previewBankImport(file, format),
    onSuccess: (res) => {
      setPreview({
        importId: res.draft.import_id,
        filename: res.draft.filename,
        candidates: res.candidates,
        reconcileToken: res.reconcile_token,
        expiresAt: res.expires_at,
      });
    },
  });

  // S-FIX-1-FE.C.1 — все строки должны иметь cp_id. Считаем unmatched.
  const unmatchedCount = preview
    ? preview.candidates.filter((c) => !(edits[c.bank_row_no] ?? c.counterparty_id ?? '').trim())
        .length
    : 0;

  const commitMut = useMutation({
    mutationFn: async () => {
      if (!preview) throw new Error('no preview');
      // S-FIX-1-FE.C.1 — НЕ фильтруем null cp_id. UI блокирует commit пока unmatched > 0.
      const payments = preview.candidates.map((c) => {
        const cpId = (edits[c.bank_row_no] ?? c.counterparty_id ?? '').trim();
        return {
          bank_row_no: c.bank_row_no,
          counterparty_id: cpId,
          amount: c.amount,
          direction: c.direction,
          posting_date: c.posting_date,
          description: c.description,
        };
      });
      return commitBankImport({
        import_id: preview.importId,
        reconcile_token: preview.reconcileToken,
        format,
        filename: preview.filename,
        payments,
      });
    },
    onSuccess: (res) => {
      alert(`Создано ${res.created_count} draft-платежей. См. /payments.`);
      setPreview(null);
      setEdits({});
    },
  });

  return (
    <div className={styles.page}>
      <PageHeader title={m.route_admin_bank_import_title()} />

      <div className={styles.toolbar}>
        <Select
          label={m.route_admin_bank_import_format_label()}
          value={format}
          onChange={(e) => setFormat(e.target.value as BankStatementFormat)}
          options={[
            { value: 'kaspi_xlsx', label: 'Kaspi Business XLSX' },
            { value: 'halyk_xlsx', label: 'Halyk XLSX' },
            { value: 'forte_xlsx', label: 'Forte XLSX' },
            { value: 'onec_kb_txt', label: '1С «Клиент-Банк» TXT' },
          ]}
        />
        <input
          ref={fileRef}
          type="file"
          accept=".xlsx,.txt"
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) previewMut.mutate(f);
          }}
          className={styles.hiddenInput}
        />
        <Button
          variant="primary"
          onClick={() => fileRef.current?.click()}
          loading={previewMut.isPending}
        >
          <FileUp size={16} /> {m.route_admin_bank_import_upload()}
        </Button>
      </div>

      {preview && (
        <div className={styles.previewCard}>
          <div className={styles.summaryHint}>
            {m.route_admin_bank_import_rows_count({ count: preview.candidates.length })}
          </div>
          <table className={styles.table}>
            <thead>
              <tr className={styles.theadRow}>
                <th className={styles.cell}>#</th>
                <th className={styles.cell}>{m.route_admin_bank_import_col_date()}</th>
                <th className={styles.cell}>{m.route_admin_bank_import_col_amount()}</th>
                <th className={styles.cell}>{m.route_admin_bank_import_col_cp()}</th>
                <th className={styles.cell}>{m.route_admin_bank_import_col_confidence()}</th>
              </tr>
            </thead>
            <tbody>
              {preview.candidates.map((c) => (
                <tr key={c.bank_row_no} className={styles.tbodyRow}>
                  <td className={styles.cell}>{c.bank_row_no}</td>
                  <td className={styles.cell}>{c.posting_date}</td>
                  <td className={styles.cellTabular}>
                    {formatMoney(c.amount)} ({c.direction})
                  </td>
                  <td className={styles.cell}>
                    <input
                      type="text"
                      placeholder={c.counterparty_id ?? 'UUID'}
                      defaultValue={c.counterparty_id ?? ''}
                      onBlur={(e) => setEdits({ ...edits, [c.bank_row_no]: e.target.value })}
                      className={styles.cpInput}
                    />
                    <div className={styles.cpRawHint}>{c.counterparty_name_raw}</div>
                  </td>
                  <td
                    className={[
                      styles.confidenceCell,
                      c.matched_counterparty_confidence >= 0.9
                        ? styles.confidenceHigh
                        : c.matched_counterparty_confidence >= 0.5
                          ? styles.confidenceMid
                          : styles.confidenceLow,
                    ].join(' ')}
                  >
                    {(c.matched_counterparty_confidence * 100).toFixed(0)}%
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {/* S-FIX-1-FE.C.1 — Lossless commit: блокируем при unmatched cp_id. */}
          {unmatchedCount > 0 && (
            <div className={styles.unmatchedAlert} role="alert">
              {m.bank_import_unmatched_rows({ count: unmatchedCount })}
            </div>
          )}
          <div className={styles.commitActions}>
            <Button
              variant="primary"
              onClick={() => commitMut.mutate()}
              loading={commitMut.isPending}
              disabled={unmatchedCount > 0}
              title={
                unmatchedCount > 0 ? m.bank_import_unmatched_rows({ count: unmatchedCount }) : ''
              }
            >
              {m.route_admin_bank_import_commit()}
            </Button>
            <Button variant="ghost" onClick={() => setPreview(null)}>
              {m.common_button_cancel()}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
